import { exportDataGrid as exportExcel } from 'devextreme/excel_exporter';
import { exportDataGrid as exportPDF } from 'devextreme/pdf_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import toUpper from 'lodash/toUpper';
import React from 'react';
import { formatDate, fromIsoString } from 'utils/dateTimeHelpers';
import { showNotification } from './notificationsHandler';

const gateDirectionColor = (value) => {
    switch (toUpper(value)) {
        case 'OUT':
            return '#f9e79f';
        case 'IN':
            return '#a2d9ce';
        default:
            return null;
    }
};

const addHeaderIcon = (cellElement) => {
    const indicators = cellElement.querySelector('.dx-column-indicators');
    if (!indicators || indicators.querySelector('.custom-header-icon')) return;

    const btn = document.createElement('span');
    btn.className = 'custom-header-icon';
    btn.textContent = '⋮';
    btn.title = 'Right click for options';
    btn.style.cursor = 'pointer';
    btn.onclick = (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const { left, top, width, height } = btn.getBoundingClientRect();
        cellElement.dispatchEvent(
            new MouseEvent('contextmenu', {
                bubbles: true,
                cancelable: true,
                view: window,
                clientX: left + width / 2,
                clientY: top + height / 2,
            })
        );
    };

    indicators.children.length ? indicators.insertBefore(btn, indicators.lastChild) : indicators.appendChild(btn);
};

export const handleCellPrepared = ({ e, theme }) => {
    if (e.rowType === 'data' && e.column.dataField === 'gateDirection') {
        const color = gateDirectionColor(e.displayValue);
        if (color) e.cellElement.style.cssText = `background-color:${color}`;
    }

    if (e.rowType === 'header' && e.column && e.cellElement) {
        const sortIcon = e.cellElement.querySelector('.dx-sort');
        const filterIcon = e.cellElement.querySelector('.dx-header-filter');
        if (sortIcon) sortIcon.setAttribute('title', 'Sort this column');
        if (filterIcon) filterIcon.setAttribute('title', 'Filter this column');
        addHeaderIcon(e.cellElement);
    } else if (e.rowType === 'header') {
        e.cellElement.style.fontWeight = theme.typography.fontWeightBold;
        e.cellElement.style.color = theme.palette.grey[100];
    }
};

export const handleContextMenuPreparing = (e) => {
    if (e.row && e.row.rowType === 'filter') return;
    if (e.target !== 'header' || !e.column) return;

    const { caption } = e.column;
    if (caption === 'Options') {
        if (e.items) e.items = e.items.filter((i) => !i.text?.toLowerCase().includes('fix') && !i.text?.toLowerCase().includes('hide'));
        return;
    }

    if (!e.items) e.items = [];
    e.items.push(
        {
            text: 'Hide Column',
            icon: 'eyeclose',
            onItemClick: () => e.component.columnOption(e.column.index, 'visible', false),
        },
        {
            text: 'Column Chooser',
            icon: 'columnchooser',
            onItemClick: () => e.component.showColumnChooser(),
        }
    );
};

// Copy logic from TableFooter, with new design the export will inside the datagrid
export const handleExporting = async ({ e, dataGridRef, fileName = 'Export', pdfExportConfig = {}, csvExportApi = null, csvExportParams = {} }) => {
    const component = dataGridRef.current?.instance();
    const defaultPdfConfig = {
        orientation: 'landscape',
        format: [1000, 210],
        defaultColumnWidth: 50,
        ignoreColumns: [],
    };
    const config = { ...defaultPdfConfig, ...pdfExportConfig };
    const ignoreColumns = config.ignoreColumns ?? [];

    const hideIgnoredColumns = () => {
        const originalStates = {};
        ignoreColumns.forEach((col) => {
            originalStates[col] = component.columnOption(col, 'visible');
            component.columnOption(col, 'visible', false);
        });
        return originalStates;
    };

    const restoreIgnoredColumns = (originalStates) => {
        ignoreColumns.forEach((col) => {
            if (originalStates[col] !== undefined) {
                component.columnOption(col, 'visible', originalStates[col]);
            }
        });
    };

    const setDefaultWidths = () => {
        const columns = component.getVisibleColumns();
        columns.forEach((col) => {
            if (!ignoreColumns.includes(col.dataField) || !col.width) {
                col.width = config.defaultColumnWidth;
            }
        });
        component.updateDimensions();
    };

    const customizeDateTime = ({ gridCell, excelCell }) => {
        const isData = gridCell.rowType === 'data';
        const isDt = ['date', 'datetime'].includes(gridCell.column.dataType);
        if (isData && isDt) {
            const parsed = fromIsoString(gridCell.value);
            if (parsed) {
                excelCell.value = formatDate(parsed, gridCell.column.format);
            }
        }
    };

    if (e.format === 'pdf') {
        const doc = new jsPDF({ orientation: config.orientation, format: config.format });
        setDefaultWidths();
        const originalStates = hideIgnoredColumns();

        await exportPDF({
            jsPDFDocument: doc,
            component,
            customizeCell: ({ pdfCell }) => {
                pdfCell.wordWrapEnabled = true;
            },
        });

        doc.save(`${fileName}.pdf`);
        restoreIgnoredColumns(originalStates);
        e.cancel = true;
    }

    if (e.format === 'xlsx') {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Main sheet');
        const originalStates = hideIgnoredColumns();

        await exportExcel({
            component,
            worksheet,
            customizeCell: customizeDateTime,
        });

        worksheet.eachRow((row) => {
            row.eachCell((cell) => {
                cell.alignment = { wrapText: true };
            });
        });

        worksheet.columns.forEach((col) => {
            if (col) {
                col.hidden = false;
                col.width = col.width || config.defaultColumnWidth;
            }
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
        restoreIgnoredColumns(originalStates);
        e.cancel = true;
    }

    if (e.format === 'csv') {
        if (typeof csvExportApi === 'function') {
            try {
                const { blob } = await csvExportApi({
                    format: 'csv',
                    fileName,
                    ...csvExportParams,
                });
                saveAs(blob, `${fileName}.csv`);
            } catch (error) {
                showNotification({
                    title: 'CSV Export Failed',
                    message: error?.message || 'Something went wrong.',
                    variant: 'error',
                });
            }
        } else {
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet('CSV Export');
            const originalStates = hideIgnoredColumns();

            await exportExcel({
                component,
                worksheet,
                customizeCell: customizeDateTime,
            });

            const buffer = await workbook.csv.writeBuffer();
            saveAs(new Blob([buffer], { type: 'text/csv;charset=utf-8;' }), `${fileName}.csv`);
            restoreIgnoredColumns(originalStates);
        }
        e.cancel = true;
    }
};

export const getVisibleDataFields = (staticCols, dynamicCols) => {
    const extractFields = (cols) => {
        const fields = [];

        const process = (col) => {
            if (!col) return;
            if (Array.isArray(col)) {
                col.forEach(process);
            } else if (col.props?.dataField) {
                fields.push(col.props.dataField);
            } else if (col.props?.children) {
                React.Children.forEach(col.props.children, (child) => {
                    if (child?.props?.dataField) fields.push(child.props.dataField);
                });
            }
        };

        cols.forEach(process);
        return fields;
    };

    return [...extractFields(staticCols), ...extractFields(dynamicCols), 'durationInYardMinutes'];
};

export const normaliseFilter = (expr) => {
    if (!Array.isArray(expr)) return expr;

    if (expr.length === 3 && expr[1] === 'anyof' && Array.isArray(expr[2])) {
        const [field, , values] = expr;
        return values.flatMap((v, i) => [...(i ? ['or'] : []), [field, '=', v]]);
    }

    return expr.reduce((acc, item, idx) => {
        const isLogical = idx % 2 === 1 && (item === 'and' || item === 'or');
        acc.push(isLogical ? item : normaliseFilter(item));
        return acc;
    }, []);
};

export const buildSearchFilter = (text, fields = []) => {
    const q = (text || '').trim();
    if (!q || !fields.length) return null;
    const perField = fields.map((f) => [f, 'contains', q]);
    return perField.reduce((acc, cur) => (acc ? [acc, 'or', cur] : cur), null);
};

export const andFilters = (a, b) => {
    if (a && b) return ['and', a, b];
    return a || b || null;
};

export const mapVisibleFields = (item, visibleFields) => {
    const mapped = {};
    visibleFields.forEach((key) => {
        mapped[key] = key.split('.').reduce((obj, k) => obj?.[k], item);
    });
    return mapped;
};

export const getSummaryRows = (data, childKeys = ['items', 'collapsedItems']) => {
    if (!Array.isArray(data)) return [];

    const isObj = (v) => v && typeof v === 'object';
    const isGroup = (n) => isObj(n) && 'key' in n && childKeys.some((k) => k in n);
    const kidsOf = (n) => (isObj(n) && childKeys.map((k) => n[k]).find(Array.isArray)) || null;

    const grouped = data.some(isGroup);
    if (!grouped) return data;

    const out = [];
    const stack = [...data];
    while (stack.length) {
        const n = stack.pop();
        const kids = kidsOf(n);
        if (kids) stack.push(...kids);
        else if (isObj(n) && !isGroup(n)) out.push(n);
    }
    return out;
};

export const onEditorPreparing = (e) => {
    if (e.parentType === 'filterRow') {
        e.updateValueTimeout = 300;
    }
};
